/* The side menu's fold, remembered per person and per screen family.
 *
 * Two decisions are remembered separately, because they are two different
 * questions. The PROOF page is where the space is worth having back (the proof
 * is a wide picture), so somebody who folds the menu there wants it folded on
 * every proof screen and open everywhere else. The key is
 * `studio-menu-fold:<userId>:proof` and `studio-menu-fold:<userId>:rest`, and
 * the user id is in the key because two people share a browser.
 *
 * Storage can refuse. A browser with cookies disabled, a private window in
 * some modes, or a full quota throws on setItem, and the menu must still work
 * when it does, so every read and write here is wrapped. */

export type MenuFoldScope = "proof" | "rest";

export const MENU_FOLD_KEY_PREFIX = "studio-menu-fold";

/* Below this width the menu has no room to be open, so it starts folded and
 * stops writing the setting down (the phone layout is row R5's business). */
export const MENU_FOLD_NARROW_WIDTH = 820;

/* Below this width the menu has no column at all: it leaves the flow and
 * becomes a drawer over the page, opened from a button in the top bar. */
export const MENU_PHONE_WIDTH = 560;

export function menuFoldKey(userId: string, scope: MenuFoldScope): string {
  return `${MENU_FOLD_KEY_PREFIX}:${userId}:${scope}`;
}

export function readMenuFold(userId: string, scope: MenuFoldScope): boolean {
  if (!userId) return false;
  try {
    return localStorage.getItem(menuFoldKey(userId, scope)) === "1";
  } catch {
    /* Unavailable storage leaves the menu open, which is the safer of the two. */
    return false;
  }
}

export function writeMenuFold(userId: string, scope: MenuFoldScope, folded: boolean): void {
  if (!userId) return;
  try {
    localStorage.setItem(menuFoldKey(userId, scope), folded ? "1" : "0");
  } catch {
    /* Nothing to do: the fold still works, it is just not remembered. */
  }
}

/** The same guard PagePanelFolds uses: a key typed into a field is the field's. */
export function isTypingTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element || typeof element.tagName !== "string") return false;
  const tag = element.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  return element.isContentEditable === true;
}

/** True for the one key that folds the menu: a bare backslash. */
export function menuFoldShortcut(event: KeyboardEvent): boolean {
  if (event.key !== "\\") return false;
  if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return false;
  if (event.isComposing) return false;
  return !isTypingTarget(event.target);
}

/** The window is narrow enough that the menu starts folded. */
export function isNarrowMenu(): boolean {
  return typeof window !== "undefined" && window.innerWidth <= MENU_FOLD_NARROW_WIDTH;
}

/** The window is narrow enough that the menu has no column and is a drawer. */
export function isPhoneMenu(): boolean {
  return typeof window !== "undefined" && window.innerWidth <= MENU_PHONE_WIDTH;
}
